import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '@/lib/i18n';

interface RegionalSEOProps {
  title: string;
  exactTitle?: boolean;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  keywords?: string;
}

/**
 * Regional SEO component that sets:
 * - hreflang tags for language/region variants
 * - canonical URLs
 * - Open Graph and Twitter meta tags
 * - Robots directives
 */
export function RegionalSEO({ 
  title,
  exactTitle = false,
  description, 
  canonicalPath,
  ogImage,
  ogType = 'website',
  noIndex = false,
  keywords,
}: RegionalSEOProps) {
  const { region, locale, config } = useI18n();
  const location = useLocation();
  
  const currentPath = canonicalPath || location.pathname;
  const cleanPath = currentPath.replace(/^\/(en|fr)/, '') || '/';
  
  // Determine the base URL and full canonical URL
  const baseUrl = region === 'US' ? 'https://mtligroup.com' : 'https://mtli.ca';
  const canonicalUrl = getCanonicalUrl(region, locale, cleanPath);
  const defaultOgImage = `${baseUrl}/og-image.png`;
  const finalOgImage = ogImage || defaultOgImage;
  // Avoid duplicating the brand name when the title already contains it
  const fullTitle = exactTitle || /mtli group/i.test(title) ? title : `${title} | MTLI Group`;
  
  useEffect(() => {
    // Update document title
    document.title = fullTitle;
    
    // Update meta description
    updateMetaTag('description', description, 'name');
    
    // Update robots meta - always set explicitly
    if (noIndex) {
      updateMetaTag('robots', 'noindex, nofollow', 'name');
    } else {
      updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1', 'name');
    }
    
    // Update keywords if provided
    if (keywords) {
      updateMetaTag('keywords', keywords, 'name');
    }
    
    // Update Open Graph tags
    updateMetaTag('og:title', fullTitle, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:type', ogType, 'property');
    updateMetaTag('og:locale', locale.replace('-', '_'), 'property');
    updateMetaTag('og:url', canonicalUrl, 'property');
    updateMetaTag('og:image', finalOgImage, 'property');
    updateMetaTag('og:site_name', 'MTLI Group', 'property');
    
    // Update Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image', 'name');
    updateMetaTag('twitter:title', fullTitle, 'name');
    updateMetaTag('twitter:description', description, 'name');
    updateMetaTag('twitter:image', finalOgImage, 'name');
    
    // Remove existing hreflang and canonical
    document.querySelectorAll('link[rel="alternate"][hreflang], link[rel="canonical"]').forEach(el => el.remove());
    
    // Add canonical URL
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = canonicalUrl;
    document.head.appendChild(canonical);
    
    // Add hreflang tags
    const hreflangLinks = getHreflangLinks(cleanPath);
    hreflangLinks.forEach(({ href, hreflang }) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = hreflang;
      link.href = href;
      document.head.appendChild(link);
    });
    
    // Cleanup on unmount
    return () => {
      document.querySelectorAll('link[rel="alternate"][hreflang], link[rel="canonical"]').forEach(el => el.remove());
    };
  }, [title, fullTitle, exactTitle, description, region, locale, cleanPath, canonicalUrl, finalOgImage, ogType, noIndex]);
  
  return null;
}

function updateMetaTag(identifier: string, content: string, type: 'name' | 'property') {
  const selector = type === 'name' 
    ? `meta[name="${identifier}"]` 
    : `meta[property="${identifier}"]`;
  
  let meta = document.querySelector(selector);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(type, identifier);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function getCanonicalUrl(region: string, locale: string, path: string): string {
  if (region === 'US') {
    return `https://mtligroup.com${path}`;
  }
  
  // Canada - include language prefix
  const langPrefix = locale === 'fr-CA' ? '/fr' : '/en';
  return `https://mtli.ca${langPrefix}${path === '/' ? '' : path}`;
}

function getHreflangLinks(path: string): Array<{ href: string; hreflang: string }> {
  const normalizedPath = path === '/' ? '' : path;
  
  return [
    // Canadian English
    { href: `https://mtli.ca/en${normalizedPath}`, hreflang: 'en-CA' },
    // Canadian French
    { href: `https://mtli.ca/fr${normalizedPath}`, hreflang: 'fr-CA' },
    // US English
    { href: `https://mtligroup.com${normalizedPath || '/'}`, hreflang: 'en-US' },
    // x-default (Canadian English as default)
    { href: `https://mtli.ca/en${normalizedPath}`, hreflang: 'x-default' },
  ];
}

export default RegionalSEO;
